import { db } from '@/core/db/dexie'
import { syncEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import type { OutCarreteiro, OutViagem, RejeicaoCarreteiro } from '@/core/db/entities'
import type { FichaCarreteiroDto, ViagemCarreteiroDto } from '@/types/dtos'
import { RECUSAS_SEM_CORRECAO, classificarFalhaSync } from '@/features/carreteiro/carreteiroRegras'

/**
 * Push de fichas de carreteiro pendentes. Diferente do producaoSync:
 *  - 1 chamada por ficha (não batch) — backend processa ficha+viagens em transação.
 *  - Tudo-ou-nada: ficha vai com todas as viagens.
 *
 * Resultado de cada envio:
 *  - 2xx → SYNCED
 *  - sem rede / 401 / 408 / 429 / 5xx → PENDING (reenvia no próximo ciclo)
 *  - demais 4xx → REJEITADA: recusa de negócio (medição gerada, contrato,
 *    viagem inválida…). Reenviar não resolve; a ficha sai da fila e aparece
 *    em "Recusadas pelo servidor" na lista do carreteiro.
 */

let runningPromise: Promise<PushSummary> | null = null

export interface PushSummary {
  attempted: number
  accepted: number
  /** Falhas que vão ser reenviadas (rede, 5xx). */
  failed: number
  /** Recusas de negócio — não reenviam. */
  rejected: number
  errors: string[]
  /** Fichas de frota própria aceitas e guardadas no servidor até a parte diária do dia existir. */
  aguardandoParteDiaria?: number
}

export async function pushCarreteiroOutbox(): Promise<PushSummary> {
  if (runningPromise) return runningPromise
  runningPromise = doPush().finally(() => {
    runningPromise = null
  })
  return runningPromise
}

async function doPush(): Promise<PushSummary> {
  const summary: PushSummary = { attempted: 0, accepted: 0, failed: 0, rejected: 0, errors: [] }

  // Recovery: fichas presas em SYNCING (tab fechada mid-upload) voltam pra PENDING.
  await db.out_carreteiro.where('syncStatus').equals('SYNCING').modify({ syncStatus: 'PENDING' })

  // FAILED = estado das versões anteriores; é reenviado uma vez e reclassificado.
  const pendingFichas = await db.out_carreteiro.where('syncStatus').anyOf('PENDING', 'FAILED').toArray()

  if (pendingFichas.length === 0) return summary
  summary.attempted = pendingFichas.length

  // Sequencial — o backend faz transação por ficha; paralelo só satura o pool.
  for (const ficha of pendingFichas) {
    await db.out_carreteiro.update(ficha.id, { syncStatus: 'SYNCING' })
    const viagens = await db.out_viagem.where('carreteiroId').equals(ficha.id).toArray()
    const now = Date.now()

    let dto: FichaCarreteiroDto
    try {
      dto = buildFichaDto(ficha, viagens)
    } catch (e) {
      await rejeitar(ficha, {
        code: 'FICHA_INCOMPLETA',
        message: e instanceof Error ? e.message : String(e),
        viagemId: null,
        campo: null,
        corrigivel: !!ficha.draftState,
      })
      summary.rejected += 1
      summary.errors.push(e instanceof Error ? e.message : String(e))
      continue
    }

    try {
      const resposta = (await syncEndpoints.carreteiro(dto)) as { aguardandoParteDiaria?: boolean; motivo?: string | null }
      // Frota própria sem a parte diária do dia: o servidor GUARDOU a ficha e vai
      // anexar sozinho. Para o aparelho o envio terminou (SYNCED) — nada de reenviar.
      const aguardando = resposta?.aguardandoParteDiaria === true
      await db.out_carreteiro.update(ficha.id, {
        syncStatus: 'SYNCED',
        lastSyncAttempt: now,
        failureReason: null,
        rejeicao: null,
        aguardandoParteDiaria: aguardando,
        aguardandoMotivo: aguardando ? (resposta.motivo ?? null) : null,
      })
      summary.accepted += 1
      if (aguardando) summary.aguardandoParteDiaria = (summary.aguardandoParteDiaria ?? 0) + 1
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0
      const reason = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message
      if (err instanceof ApiError && classificarFalhaSync(status) === 'REJEITADA') {
        const rejeicao = montarRejeicao(err, !!ficha.draftState)
        await rejeitar(ficha, rejeicao)
        summary.rejected += 1
        summary.errors.push(rejeicao.message)
      } else {
        await db.out_carreteiro.update(ficha.id, {
          syncStatus: 'PENDING',
          lastSyncAttempt: now,
          failureReason: reason,
          retryCount: (ficha.retryCount ?? 0) + 1,
        })
        summary.failed += 1
        summary.errors.push(reason)
      }
    }
  }

  return summary
}

async function rejeitar(ficha: OutCarreteiro, rejeicao: RejeicaoCarreteiro) {
  await db.out_carreteiro.update(ficha.id, {
    syncStatus: 'REJEITADA',
    lastSyncAttempt: Date.now(),
    failureReason: rejeicao.message,
    rejeicao,
    retryCount: (ficha.retryCount ?? 0) + 1,
  })
}

/** Resposta de erro do backend → recusa guardada na ficha. */
function montarRejeicao(err: ApiError, podeReabrir: boolean): RejeicaoCarreteiro {
  const code = err.code ?? `HTTP_${err.status}`
  const details = (err.details && typeof err.details === 'object' ? err.details : {}) as Record<string, unknown>
  let message = err.message
  if (code === 'VALIDATION_ERROR') {
    const campos = Object.keys(details)
    message = campos.length
      ? `Dados inválidos no envio (${campos.join(', ')}). Corrija e salve de novo.`
      : 'Dados inválidos no envio. Corrija e salve de novo.'
  }
  return {
    code,
    message,
    viagemId: typeof details.viagemId === 'string' ? details.viagemId : null,
    campo: typeof details.campo === 'string' ? details.campo : null,
    corrigivel: podeReabrir && !(RECUSAS_SEM_CORRECAO as readonly string[]).includes(code),
  }
}

function buildFichaDto(ficha: OutCarreteiro, viagens: OutViagem[]): FichaCarreteiroDto {
  if (ficha.mveId == null || ficha.obraId == null) {
    throw new Error('Ficha incompleta no outbox — obra ou caminhão faltando')
  }
  if (viagens.length === 0) {
    throw new Error('Ficha sem viagens no outbox')
  }
  return {
    id: ficha.id,
    tipo: ficha.tipo,
    mveId: ficha.mveId,
    obraId: ficha.obraId,
    // Informativos — o servidor recalcula o chefe.
    chefeEquipeId: ficha.chefeEquipeId ?? null,
    chefeEquipeNome: ficha.chefeEquipeNome ?? null,
    cadastradorId: ficha.cadastradorId ?? null,
    dtMovimentacao: ficha.dt,
    // ficha + chapaMotorista: removidos do form; outboxes antigos podem ter valor.
    ficha: ficha.ficha ?? null,
    kmInicial: ficha.kmInicial,
    kmFinal: ficha.kmFinal,
    chapaEncarregado: ficha.chapaEncarregado,
    chapaMotorista: ficha.chapaMotorista ?? null,
    // A flag real é a das viagens (outboxes antigos gravavam boolean fixo).
    fichaConcreto: viagens[0]?.concreto ?? 'N',
    capturedAt: ficha.capturedAt,
    deletado: 'N',
    viagens: viagens.map(mapViagem),
  }
}

function mapViagem(v: OutViagem): ViagemCarreteiroDto {
  return {
    id: v.id,
    servicoExecutadoId: v.servicoExecutadoId,
    servicoExecutadoNome: v.servicoExecutadoNome,
    contratoItemId: v.contratoItemId,
    mveId: v.mveId,
    materialId: v.materialId ?? null,
    materialNome: v.materialNome ?? null,
    localCargaId: v.localCargaId,
    localCargaNome: v.localCargaNome,
    cargaChapaApontador: v.cargaChapaApontador,
    cargaHora: v.cargaHora,
    cargaEstacaOrigem: v.cargaEstacaOrigem,
    cargaDtFixo: v.cargaDtFixo,
    localDescargaId: v.localDescargaId,
    localDescargaNome: v.localDescargaNome,
    descargaChapaApontador: v.descargaChapaApontador,
    descargaHora: v.descargaHora,
    descargaEstacaDescarga: v.descargaEstacaDescarga,
    descargaBairro: v.descargaBairro,
    descargaRua: v.descargaRua,
    descargaLargura: v.descargaLargura,
    descargaComprimento: v.descargaComprimento,
    descargaPosicao: v.descargaPosicao as ViagemCarreteiroDto['descargaPosicao'],
    qdeVg: 1,
    producao: v.producao,
    concreto: v.concreto,
    nf: v.nf,
    volume: v.volume,
    deletado: v.deletado,
  }
}
